import { Injectable } from '@angular/core';
import { environment } from '@env/environment';

/**
 * Service to handle Design Huddle and Inksoft image URL resolution
 * Provides centralized logic for displaying product images from both systems
 */
@Injectable({
  providedIn: 'root'
})
export class DesignHuddleImageService {

  constructor() { }

  /**
   * Get product thumbnail URL - prioritizes Design Huddle Azure Blob URLs
   * @param product Product object with possible Design Huddle or Inksoft URLs
   * @returns Full image URL
   */
  getProductThumbnailUrl(product: any): string {
    // Priority 1: Design Huddle Azure Blob thumbnail
    if (product?.AzureBlobThumbnailUrl) {
      return product.AzureBlobThumbnailUrl;
    }

    // Priority 2: Inksoft thumbnail
    if (product?.ThumbnailRelativeUrl) {
      return `${environment.imgBaseURL}${product.ThumbnailRelativeUrl}`;
    }

    // Priority 3: First color image
    if (product?.ColorList?.[0]?.Images?.[0]?.Image) {
      return product.ColorList[0].Images[0].Image;
    }

    // Priority 4: PNG relative URL with product
    if (product?.PngRelativeUrlWithProduct) {
      return `${environment.imgBaseURL}${product.PngRelativeUrlWithProduct}`;
    }

    // Fallback
    return 'assets/img/placeholder-product.png';
  }

  /**
   * Get product print-ready file URL (Design Huddle only)
   * @param product Product object
   * @returns Print file URL or null
   */
  getPrintFileUrl(product: any): string | null {
    if (product?.AzureBlobPrintFileUrl) {
      return product.AzureBlobPrintFileUrl;
    }

    // Inksoft print files
    if (product?.PngRelativeUrl) {
      return `${environment.imgBaseURL}${product.PngRelativeUrl}`;
    }

    return null;
  }

  /**
   * Get product main display image
   * Used for product detail pages and main displays
   */
  getProductMainImage(product: any): string {
    // Design Huddle products - use Azure Blob thumbnail
    if (product?.AzureBlobThumbnailUrl) {
      return product.AzureBlobThumbnailUrl;
    }

    // Inksoft products - use PNG with product
    if (product?.PngRelativeUrlWithProduct) {
      return `${environment.imgBaseURL}${product.PngRelativeUrlWithProduct}`;
    }

    // Inksoft thumbnail
    if (product?.ThumbnailRelativeUrl) {
      return `${environment.imgBaseURL}${product.ThumbnailRelativeUrl}`;
    }

    return 'assets/img/placeholder-product.png';
  }

  /**
   * Check if product uses Design Huddle
   */
  isDesignHuddleProduct(product: any): boolean {
    return !!(
      product?.DesignHuddleProjectId ||
      product?.AzureBlobThumbnailUrl ||
      product?.AzureBlobPrintFileUrl
    );
  }

  /**
   * Check if product uses Inksoft
   */
  isInksoftProduct(product: any): boolean {
    return !!(
      product?.DesignId ||
      product?.ThumbnailRelativeUrl ||
      product?.PngRelativeUrl
    ) && !this.isDesignHuddleProduct(product);
  }

  /**
   * Get product type label
   */
  getProductTypeLabel(product: any): string {
    if (this.isDesignHuddleProduct(product)) {
      return 'Design Huddle';
    }
    if (this.isInksoftProduct(product)) {
      return 'Inksoft';
    }
    return 'Product';
  }

  /**
   * Get all product images for display (gallery)
   * Combines Design Huddle and custom uploaded images
   */
  getProductGalleryImages(product: any, customImages: any[] = []): any[] {
    const images: any[] = [];

    // Add Design Huddle thumbnail if available
    if (product?.AzureBlobThumbnailUrl) {
      images.push({
        Image: product.AzureBlobThumbnailUrl,
        IsDefault: true,
        IsActive: true,
        Id: -1,
        Source: 'DesignHuddle',
        AzureBlobUrl: product.AzureBlobThumbnailUrl
      });
    }

    // Add Design Huddle print file if available
    if (product?.AzureBlobPrintFileUrl) {
      images.push({
        Image: product.AzureBlobPrintFileUrl,
        IsDefault: false,
        IsActive: true,
        Id: -2,
        Source: 'DesignHuddle',
        AzureBlobUrl: product.AzureBlobPrintFileUrl
      });
    }

    // Add Inksoft images if no Design Huddle images
    if (images.length === 0) {
      if (product?.PngRelativeUrlWithProduct) {
        images.push({
          Image: `${environment.imgBaseURL}${product.PngRelativeUrlWithProduct}`,
          IsDefault: true,
          IsActive: true,
          Id: -3,
          Source: 'Inksoft'
        });
      }

      if (product?.ThumbnailRelativeUrl) {
        images.push({
          Image: `${environment.imgBaseURL}${product.ThumbnailRelativeUrl}`,
          IsDefault: images.length === 0,
          IsActive: true,
          Id: -4,
          Source: 'Inksoft'
        });
      }
    }

    // Add custom uploaded images
    if (customImages && customImages.length > 0) {
      customImages.forEach((img, index) => {
        if (img.IsActive) {
          images.push({
            ...img,
            Source: 'Custom'
          });
        }
      });
    }

    // If no images at all, add placeholder
    if (images.length === 0) {
      images.push({
        Image: 'assets/img/placeholder-product.png',
        IsDefault: true,
        IsActive: true,
        Id: -99,
        Source: 'Placeholder'
      });
    }

    return images;
  }

  /**
   * Get image URL for cart/order items
   */
  getCartItemImage(item: any): string {
    // Check if it's a Design Huddle product
    if (item?.AzureBlobThumbnailUrl) {
      return item.AzureBlobThumbnailUrl;
    }

    // Inksoft product
    if (item?.ThumbnailRelativeUrl) {
      return `${environment.imgBaseURL}${item.ThumbnailRelativeUrl}`;
    }

    if (item?.PngRelativeUrlWithProduct) {
      return `${environment.imgBaseURL}${item.PngRelativeUrlWithProduct}`;
    }

    return 'assets/img/placeholder-product.png';
  }
}
